/**
 * Report message & block user (docs/specs/report-block.md; App Store guideline 1.2). Core `logic/moderation` holds the
 * client rules (who can report or block, the reasons and their copy) that iOS and Android port with its vectors.
 *
 * - Reporting: anyone can report a message they can read that isn't theirs; one report per (message, reporter), a
 *   second one updates its reason and note. The message stays visible to the reporter. Owners and admins get a
 *   notification and support@bundle.direct an email; owners and admins review open reports (Settings → Admin →
 *   Reports) and resolve them (delete the message, or dismiss).
 * - Blocking: per person per workspace, silent (the blocked person isn't told). No push, notification or Activity
 *   item for the blocker from the blocked person; the blocked person can't open a new 1:1 DM with the blocker, and
 *   posting into an existing 1:1 DM between them answers 403 `blocked`. Clients collapse blocked people's messages.
 *   My list arrives in bootstrap (`blockedUserIds`) and changes as `blocks_changed` (only to my own connections).
 */
import { z } from "zod";
import { Id, Timestamp } from "./entities.ts";

export const ReportReason = z.enum(["spam", "harassment", "inappropriate", "other"]);
export type ReportReason = z.infer<typeof ReportReason>;

export const ReportStatus = z.enum(["open", "resolved"]);
export type ReportStatus = z.infer<typeof ReportStatus>;

/** What resolving a report did: nothing (dismissed) or the message was deleted. */
export const ReportAction = z.enum(["none", "message_deleted"]);
export type ReportAction = z.infer<typeof ReportAction>;

export const MAX_REPORT_NOTE = 1000;
export const MAX_REPORT_PREVIEW = 280;

/** POST /v1/w/:ws/messages/:messageId/report → 204. */
export const ReportMessageRequest = z.object({
  reason: ReportReason,
  note: z.string().max(MAX_REPORT_NOTE).optional(),
});
export type ReportMessageRequest = z.infer<typeof ReportMessageRequest>;

export const MessageReport = z.object({
  id: Id,
  messageId: Id,
  channelId: Id,
  reporterId: Id,
  /** The reported message's author. */
  authorId: Id,
  reason: ReportReason,
  note: z.string().max(MAX_REPORT_NOTE).optional(),
  status: ReportStatus,
  createdAt: Timestamp,
  resolvedAt: Timestamp.optional(),
  resolvedBy: Id.optional(),
  action: ReportAction.optional(),
  /** The message as plain text when it was reported (≤ 280 characters). */
  messagePreview: z.string().max(MAX_REPORT_PREVIEW),
});
export type MessageReport = z.infer<typeof MessageReport>;

/** GET /v1/w/:ws/reports?status=open|resolved (default open; owners and admins). */
export const ListReportsQuery = z.object({ status: ReportStatus.optional() });
export type ListReportsQuery = z.infer<typeof ListReportsQuery>;

/** Newest first. */
export const ListReportsResponse = z.object({ reports: z.array(MessageReport) });
export type ListReportsResponse = z.infer<typeof ListReportsResponse>;

/** PATCH /v1/w/:ws/reports/:reportId → MessageReport (owners and admins). */
export const ResolveReportRequest = z.object({
  status: z.literal("resolved"),
  action: ReportAction.optional(),
});
export type ResolveReportRequest = z.infer<typeof ResolveReportRequest>;

/** GET /v1/w/:ws/blocks — the people I blocked in this workspace. */
export const ListBlocksResponse = z.object({ userIds: z.array(Id) });
export type ListBlocksResponse = z.infer<typeof ListBlocksResponse>;
